import React, { useState, useEffect } from "react";
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Form from "react-bootstrap/Form";
import Button from "react-bootstrap/Button";
import Card from "react-bootstrap/Card";
import { ToastContainer, toast } from "react-toastify";
import axios from "axios";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { FaFilePdf } from "react-icons/fa";
import AppDataGrid from "../../components/common/AppDataGrid";
import Tooltip from "@mui/material/Tooltip";
import excelIcon from "../../assets/icons/excel.png";

const EmployeeAttendanceReport = () => {
  const userData = sessionStorage.getItem("user");
  const userObj = userData ? JSON.parse(userData) : {};

  const baseUrl = process.env.REACT_APP_API_BASE_URL;
  const currentYear = new Date().getFullYear();
  const [monthList, setMonthList] = useState([]);
  const [form, setForm] = useState({
    month_id: "",
  });
  

  const [year, setYear] = useState(currentYear);
  const [yearList, setYearList] = useState([]);



  const [employeesData, setEmployeesData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
 
  // const [year, setYear] = useState(currentYear);

  const searchableColumns = ["emp_code", "emp_name"];

  const handleInputChange = (e) => {
    const { id, value } = e.target;
    setForm((prev) => ({
      ...prev,
      [id]: value,
    }));
  };

  const getSelectedMonthName = () => {
    const match = monthList.find(
      (m) => String(m.month_id) === String(form.month_id)
    );
    return match?.month_name || "";
  };

  const generateYears = (start = currentYear - 2, end = currentYear + 2) => {
    const years = [];
    for (let y = start; y <= end; y++) {
      years.push(y);
    }
    return years;
  };


  const fetchData = async (monthIdParam) => {
    const selectedMonthId = monthIdParam || form.month_id;
    if (!selectedMonthId) return;

    const monthNumber = Number(selectedMonthId);
   

    setIsLoading(true);
    try {
      const payload = {
        month: monthNumber,
        year: year,  
        school_id: userObj.school_id,
      };

      const response = await axios.post(
        `${baseUrl}/employeeattendancereport/`,
        payload,
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      const data = Array.isArray(response.data) ? response.data : [];

      const mapped = data.map((row) => ({
        ...row,
        emp_code: row.emp_code || row.emp_id,
      }));

      setEmployeesData(mapped);
    } catch (error) {
      console.error("Error fetching employee attendance summary:", error);
      toast.error("Something went wrong while fetching employee attendance.");
      setEmployeesData([]);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchMonths = async () => {
    try {
      const response = await axios.post(baseUrl + "/months/", {
        action: "READ",
      });
      const list = Array.isArray(response.data) ? response.data : [];
      setMonthList(list);

      const currentMonthNumber = new Date().getMonth() + 1;
      const currentMonthObj =
        list.find((m) => Number(m.month_id) === currentMonthNumber) || list[0];

      if (currentMonthObj) {
        const idStr = String(currentMonthObj.month_id);
        setForm((prev) => ({
          ...prev,
          month_id: idStr,
        }));
        fetchData(idStr);
      }
    } catch (error) {
      console.error("Failed to load months:", error);
      toast.error("Failed to load months.");
    }
  };

  useEffect(() => {
    setYearList(generateYears());
    fetchMonths();
  }, []);


  const handleSubmit = async (e) => {
    e.preventDefault();
    await fetchData();
  };

  const exportToExcel = () => {
    if (!employeesData.length) {
      toast.error("No data available to export.");
      return;
    }

    const monthName = getSelectedMonthName();

    const exportData = employeesData.map((row) => ({
      "Emp Code": row.emp_code || "-",
      "Emp Name": row.emp_name || "-",
      "Total Working Days": row.total_working_days ?? "-",
      "Present Days": row.present_days ?? "-",
      "Absent Days": row.absent_days ?? "-",
      Leaves: row.leaves ?? "-",
      "Payable Days": row.payable_days ?? "-",
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Employee-Attendance");
    XLSX.writeFile(
      wb,
      `employee_attendance_report_${monthName || "Month"}.xlsx`
    );
  };

  const exportToPDF = () => {
    if (!employeesData.length) {
      toast.error("No data available to export.");
      return;
    }

    const monthName = getSelectedMonthName();

    const doc = new jsPDF();
    doc.text(
      `Employee Attendance Report - ${monthName || ""}`,
      14,
      10
    );

    const tableColumn = [
      "Emp Code",
      "Emp Name",
      "Total Working Days",
      "Present Days",
      "Absent Days",
      "Leaves",
      "Payable Days",
    ];

    const tableRows = employeesData.map((row) => [
      row.emp_code || "-",
      row.emp_name || "-",
      row.total_working_days ?? "-",
      row.present_days ?? "-",
      row.absent_days ?? "-",
      row.leaves ?? "-",
      row.payable_days ?? "-",
    ]);

    autoTable(doc, {
      head: [tableColumn],
      body: tableRows,
      startY: 20,
      styles: { fontSize: 10 },
      headStyles: { fillColor: [0, 123, 255] },
    });

    doc.save(
      `employee_attendance_report_${monthName || "Month"}_${new Date()
        .toISOString()
        .slice(0, 10)}.pdf`
    );
  };

  const normalizedQuery = (searchQuery || "").toLowerCase().trim();
  const filteredEmployees = employeesData.filter((row) =>
    searchableColumns.some((col) =>
      (row[col] || "")
        .toString()
        .toLowerCase()
        .includes(normalizedQuery)
    )
  );

  const columns = [
    {
      field: "emp_code",
      headerName: "Emp Id",
      flex: 1,
      headerAlign: "center",
      align: "center",
      sortable: true,
      renderCell: (params) => (
        <Tooltip title={params.value || ""}>
          <span>{params.value || ""}</span>
        </Tooltip>
      ),
    },
    {
      field: "emp_name",
      headerName: "Emp Name",
      flex: 1.5,
      headerAlign: "center",
      align: "center",
      sortable: true,
      renderCell: (params) => (
        <Tooltip title={params.value || ""}>
          <span>{params.value || ""}</span>
        </Tooltip>
      ),
    },
    {
      field: "total_working_days",
      headerName: "Total Working Days",
      flex: 1,
      headerAlign: "center",
      align: "center",
      sortable: true,
      renderCell: (params) => (
        <Tooltip title={params.value?.toString() || ""}>
          <span>{params.value ?? ""}</span>
        </Tooltip>
      ),
    },
    {
      field: "present_days",
      headerName: "Present Days",
      flex: 1,
      headerAlign: "center",
      align: "center",
      sortable: true,
      renderCell: (params) => (
        <Tooltip title={params.value?.toString() || ""}>
          <span>{params.value ?? ""}</span>
        </Tooltip>
      ),
    },
    {
      field: "absent_days",
      headerName: "Absent Days",
      flex: 1,
      headerAlign: "center",
      align: "center",
      sortable: true,
      renderCell: (params) => (
        <Tooltip title={params.value?.toString() || ""}>
          <span>{params.value ?? ""}</span>
        </Tooltip>
      ),
    },
    {
      field: "leaves",
      headerName: "Leaves",
      flex: 1,
      headerAlign: "center",
      align: "center",
      sortable: true,
      renderCell: (params) => (
        <Tooltip title={params.value?.toString() || ""}>
          <span>{params.value ?? ""}</span>
        </Tooltip>
      ),
    },
    {
      field: "payable_days",
      headerName: "Payable Days",
      flex: 1,
      headerAlign: "center",
      align: "center",
      sortable: true,
      renderCell: (params) => (
        <Tooltip title={params.value?.toString() || ""}>
          <span>{params.value ?? ""}</span>
        </Tooltip>
      ),
    },
  ];

  return (
    <div className="pageBody">
      <ToastContainer />
      <Container fluid>
        <Card>
          <Card.Body>
            <form onSubmit={handleSubmit}>
              <Row>
                <Col xs={12}>
                  <h6 className="commonSectionTitle">
                    Employee Attendance Report
                  </h6>
                </Col>
              </Row>

              <Row className="align-items-end">
                <Col xs={12} md={6} lg={4} xxl={4}>
                  <div className="commonInput">
                    <Form.Group>
                      <Form.Label>
                        Month <span className="requiredStar">*</span>
                      </Form.Label>
                      <Form.Select
                        required
                        id="month_id"
                        value={form.month_id}
                        onChange={handleInputChange}
                      >
                        <option value="" disabled hidden>Select Month</option>
                        {(monthList || []).map((m) => (
                          <option key={m.month_id} value={m.month_id}>
                            {m.month_name}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </div>
                </Col>
                <Col xs={12} md={6} lg={4} xxl={4}>
                  <div className="commonInput">
                    <Form.Group>
                      <Form.Label>
                        Year <span className="requiredStar">*</span>
                      </Form.Label>
                      <Form.Select
                        value={year}
                        onChange={(e) => setYear(Number(e.target.value))}
                        required
                      >
                        {yearList.map((y) => (
                          <option key={y} value={y}>
                            {y}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </div>
                </Col>



                <Col xs="auto" className="d-flex justify-content-end">
                  <Button
                    type="submit"
                    variant="success"
                    className="btn btn-success primaryBtn"
                    style={{ height: "36px", marginBottom: "18px" }}
                  >
                    Search
                  </Button>
                </Col>
              </Row>
            </form>
          </Card.Body>
        </Card>

        <div className="d-flex justify-content-between align-items-center mt-3">
          <input
            type="text"
            placeholder="Search..."
            value={searchQuery}
            className="form-control form-control-sm w-25"
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          <div>
            <Button
              className="btnMain"
              variant="success"
              onClick={exportToExcel}
              style={{ marginRight: "10px" }}
            >
              <img
                src={excelIcon}
                alt="Download Excel"
                style={{ width: "20px" }}
              />
            </Button>
            <Button className="btnMain" variant="danger" onClick={exportToPDF}>
              <FaFilePdf style={{ marginRight: "5px" }} />
            </Button>
          </div>
        </div>

        <div className="commonTable height100 mt-2">
          <div className="tableBody">
            <div style={{ width: "100%", overflowX: "auto" }}>
              <AppDataGrid
                rows={filteredEmployees.length ? filteredEmployees : []}
                columns={columns}
                getRowId={(row) =>
                  row.emp_id ||
                  row.emp_code ||
                  `${row.emp_name}_${row.emp_code}`
                }
                pageSizeOptions={[10, 20, 50]}
                showToolbar={false}
                pagination
                density="compact"
                maxHeight={400}
              />
            </div>
          </div>
        </div>
      </Container>
    </div>
  );
};

export default EmployeeAttendanceReport;
